import type { CalibrationPoint, UnitSystem } from '../types/project';

export const UNIT_SYSTEM_OPTIONS: {
  value: UnitSystem;
  label: string;
  short: string;
}[] = [
  { value: 'metric_m', label: 'Meters', short: 'm' },
  { value: 'metric_cm', label: 'Centimeters', short: 'cm' },
  { value: 'imperial_ft', label: 'Feet', short: 'ft' },
];

export function unitLabel(unit: UnitSystem): string {
  return UNIT_SYSTEM_OPTIONS.find((o) => o.value === unit)?.label ?? 'meters';
}

export function unitShort(unit: UnitSystem): string {
  return UNIT_SYSTEM_OPTIONS.find((o) => o.value === unit)?.short ?? 'm';
}

export function distanceToMeters(distance: number, unit: UnitSystem): number {
  switch (unit) {
    case 'metric_m':
      return distance;
    case 'metric_cm':
      return distance / 100;
    case 'imperial_ft':
      return distance * 0.3048;
  }
}

export function metersToUnit(meters: number, unit: UnitSystem): number {
  switch (unit) {
    case 'metric_m':
      return meters;
    case 'metric_cm':
      return meters * 100;
    case 'imperial_ft':
      return meters / 0.3048;
  }
}

export function pixelDistance(
  p1: CalibrationPoint,
  p2: CalibrationPoint,
): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

export function computePixelsPerMeter(
  p1: CalibrationPoint,
  p2: CalibrationPoint,
  distance: number,
  unit: UnitSystem,
): number {
  const px = pixelDistance(p1, p2);
  const meters = distanceToMeters(distance, unit);
  if (meters <= 0) return 0;
  return px / meters;
}

export function formatScale(
  pixelsPerMeter: number,
  unit: UnitSystem,
): string {
  if (pixelsPerMeter <= 0) return 'Not calibrated';
  const pxPerUnit = pixelsPerMeter * distanceToMeters(1, unit);
  return `${pxPerUnit.toFixed(1)} px per ${unitShort(unit)}`;
}

export function formatRouteLength(meters: number, unit: UnitSystem): string {
  const value = metersToUnit(meters, unit);
  const suffix = unitShort(unit);
  if (value >= 100) return `${value.toFixed(0)} ${suffix}`;
  if (value >= 10) return `${value.toFixed(1)} ${suffix}`;
  return `${value.toFixed(2)} ${suffix}`;
}
